import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { DialogHeaderWithClose } from "../Dialog/DialogHeaderWithClose.component";
import { formatSeriesConflictDateTime } from "./functions/format-series-conflict-date-time.function";
import { seriesEditConflictRowSecondary } from "./functions/series-edit-conflict-row-secondary.function";
import type {
  SeriesBookingTimeConflict,
  UpdateSeriesBookingTimesResponse
} from "../../features/Schedule/schedule.types";
import useTimeFormat from "../../hooks/useTimeFormat/useTimeFormat";
import { isSeriesDialogBackdropCloseReason } from "./functions/is-series-dialog-backdrop-close-reason.function";

type P = {
  open: boolean;
  conflict: UpdateSeriesBookingTimesResponse | null;
  selectedDateIso: string;
  timezone: string;
  selectedDayIsFree: boolean;
  onClose: () => void;
  onUpdateFreeDays: () => void;
  onOnlySelectedDay: () => void;
  onChangeTimes: () => void;
};

function SeriesEditConflictBlockedTable({
  blocked,
  timezone,
  dateTimeFormat,
  bookedBy
}: Readonly<{
  blocked: SeriesBookingTimeConflict[];
  timezone: string;
  dateTimeFormat: string;
  bookedBy: (name: string) => string;
}>) {
  const { t } = useTranslation();

  return (
    <Table
      size="small"
      data-testid="schedule-series-edit-conflict-blocked-list"
      sx={{ "& td, & th": { borderBottom: 1, borderColor: "divider" } }}
    >
      <TableHead>
        <TableRow>
          <TableCell component="th" variant="head">
            {t("Date and Time")}
          </TableCell>
          <TableCell component="th" variant="head">
            {t("_schedule_seriesEditConflictWhy")}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {blocked.map(item => (
          <TableRow key={item.bookingId}>
            <TableCell>
              {formatSeriesConflictDateTime(item.start, timezone, dateTimeFormat)}
            </TableCell>
            <TableCell>{seriesEditConflictRowSecondary(item, bookedBy)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function ScheduleSeriesEditConflictDialog({
  open,
  conflict,
  selectedDateIso,
  timezone,
  selectedDayIsFree,
  onClose,
  onUpdateFreeDays,
  onOnlySelectedDay,
  onChangeTimes
}: Readonly<P>) {
  const { t } = useTranslation();
  const { dateFormat, timeFormat } = useTimeFormat();
  const dateTimeFormat = `${dateFormat} ${timeFormat}`;
  const selectedDateLabel = DateTime.fromISO(selectedDateIso)
    .setZone(timezone)
    .toFormat("ccc, d LLL");

  const handleDialogClose = (_event: object, reason: "backdropClick" | "escapeKeyDown") => {
    if (isSeriesDialogBackdropCloseReason(reason)) {
      return;
    }
    onClose();
  };

  const blocked = conflict?.blockedBookings ?? [];
  const freeCount = conflict?.freeBookingIds?.length ?? 0;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      aria-labelledby="schedule-series-edit-conflict-title"
      data-testid="schedule-series-edit-conflict-dialog"
      maxWidth="sm"
      fullWidth
    >
      <DialogHeaderWithClose
        titleId="schedule-series-edit-conflict-title"
        title={t("_schedule_seriesEditConflictTitle")}
        onClose={onClose}
        closeTestId="schedule-series-edit-conflict-close"
      />
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t("_schedule_seriesEditConflictBody", { freeCount, blockedCount: blocked.length })}
        </DialogContentText>
        {blocked.length > 0 && (
          <SeriesEditConflictBlockedTable
            blocked={blocked}
            timezone={timezone}
            dateTimeFormat={dateTimeFormat}
            bookedBy={name => t("_schedule_seriesEditConflictBookedBy", { name })}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1, justifyContent: "flex-end" }}>
        <Button
          onClick={onClose}
          data-testid="schedule-series-edit-conflict-abort"
          color="secondary"
        >
          {t("Cancel")}
        </Button>
        <Button
          onClick={onChangeTimes}
          data-testid="schedule-series-edit-conflict-change-times"
          color="secondary"
        >
          {t("_schedule_seriesEditConflictChangeTimes")}
        </Button>
        {selectedDayIsFree && (
          <Button
            onClick={onOnlySelectedDay}
            data-testid="schedule-series-edit-conflict-only-day"
            color="secondary"
          >
            {t("_schedule_seriesEditConflictOnlySelectedDay", { date: selectedDateLabel })}
          </Button>
        )}
        {freeCount > 0 && (
          <Button
            onClick={onUpdateFreeDays}
            variant="contained"
            data-testid="schedule-series-edit-conflict-free-days"
          >
            {t("_schedule_seriesEditConflictUpdateFreeDays")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
